import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from './ui/use-toast';
import { api, endpoints } from '../services/api';
import { Upload, Download, Trash2, FileText, File, FileImage, ExternalLink } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const AsosDocumentUpload = ({ caseId, currentDocument, onDocumentChange }) => {
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [openingLink, setOpeningLink] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      console.log('📤 ASOS belgesi yükleniyor:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        caseId: caseId,
        endpoint: endpoints.cases.uploadAsosDocument(caseId)
      });
      
      const formData = new FormData();
      formData.append('document', file);
      
      return api.post(endpoints.cases.uploadAsosDocument(caseId), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    onSuccess: (response) => {
      console.log('✅ ASOS belgesi yükleme başarılı:', response.data);
      toast({
        title: "Başarılı",
        description: "ASOS belgesi başarıyla yüklendi",
        variant: "success"
      });
      // ASOS belge yükleme sonrası tüm ilgili cache'leri invalidate et
      queryClient.invalidateQueries(['case', caseId]);
      queryClient.invalidateQueries(['cases']); // Vaka listesi
      queryClient.invalidateQueries(['plannedCases']); // Planlı vakalar
      if (onDocumentChange) {
        onDocumentChange(response.data.data);
      }
    },
    onError: (error) => {
      console.error('❌ ASOS belgesi yükleme hatası:', {
        error: error,
        response: error.response,
        message: error.response?.data?.message,
        status: error.response?.status
      });
      toast({
        title: "Hata",
        description: error.response?.data?.message || "ASOS belgesi yüklenirken hata oluştu",
        variant: "destructive"
      });
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => api.delete(endpoints.cases.deleteAsosDocument(caseId)),
    onSuccess: () => {
      toast({
        title: "Başarılı",
        description: "ASOS belgesi başarıyla silindi",
        variant: "success"
      });
      // ASOS belge silme sonrası tüm ilgili cache'leri invalidate et
      queryClient.invalidateQueries(['case', caseId]);
      queryClient.invalidateQueries(['cases']); // Vaka listesi
      queryClient.invalidateQueries(['plannedCases']); // Planlı vakalar
      if (onDocumentChange) {
        onDocumentChange(null);
      }
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "ASOS belgesi silinirken hata oluştu",
        variant: "destructive"
      });
    }
  });

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    console.log('📁 ASOS dosyası seçildi:', file);
    
    if (!file) {
      console.log('❌ Dosya seçilmedi');
      return;
    }

    // Input'u temizle ki aynı dosyayı tekrar seçebilsin
    event.target.value = '';

    console.log('📋 ASOS dosya detayları:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });

    // Dosya boyutu kontrolü (10MB)
    if (file.size > 10 * 1024 * 1024) {
      console.log('❌ Dosya çok büyük:', file.size);
      toast({
        title: "Hata",
        description: "Dosya boyutu 10MB'dan büyük olamaz",
        variant: "destructive"
      });
      return;
    }

    // Dosya tipi kontrolü
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      console.log('❌ Dosya tipi desteklenmiyor:', file.type);
      toast({
        title: "Hata", 
        description: "Sadece PDF, JPG, PNG, DOC, DOCX dosyaları yüklenebilir",
        variant: "destructive"
      });
      return;
    }

    console.log('✅ ASOS dosyası geçti, upload başlatılıyor...');
    uploadMutation.mutate(file);
  };

  const handleDownload = async () => {
    if (!currentDocument?.asos_document_filename) return;
    
    setDownloading(true);
    try {
      const response = await api.get(endpoints.cases.getAsosDocument(caseId), {
        responseType: 'blob'
      });
      
      // Blob'u indirilebilir link'e çevir
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = currentDocument.asos_document_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      toast({
        title: "Hata",
        description: "ASOS belgesi indirilirken hata oluştu",
        variant: "destructive"
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = () => {
    if (!currentDocument?.asos_document_filename) return;
    
    if (window.confirm(`"${currentDocument.asos_document_filename}" ASOS belgesini silmek istediğinizden emin misiniz?`)) {
      deleteMutation.mutate();
    }
  };

  const handleOpenLink = async () => {
    if (!currentDocument?.asos_document_filename) return;
    
    setOpeningLink(true);
    try {
      const response = await api.get(endpoints.cases.getAsosDocument(caseId), {
        responseType: 'blob'
      });
      // Dosya uzantısından MIME belirle (sunucu octet-stream gönderse bile resim/PDF doğru açılsın)
      const contentType = getMimeFromFilename(currentDocument.asos_document_filename);
      const dataBlob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      const arrayBuffer = await dataBlob.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      toast({
        title: "Hata",
        description: "ASOS belgesi açılırken hata oluştu",
        variant: "destructive"
      });
    } finally {
      setOpeningLink(false);
    }
  };

  const getMimeFromFilename = (filename) => {
    const ext = (filename || '').split('.').pop()?.toLowerCase();
    const mime = { pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' };
    return mime[ext] || 'application/octet-stream';
  };

  const getFileIcon = (filename) => {
    if (!filename) return <File className="h-5 w-5" />;
    
    const extension = filename.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return <FileText className="h-5 w-5 text-red-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <FileImage className="h-5 w-5 text-blue-500" />;
      case 'doc':
      case 'docx':
        return <FileText className="h-5 w-5 text-blue-600" />;
      default:
        return <File className="h-5 w-5" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  };

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-orange-300 rounded-lg p-6 text-center bg-orange-50">
        {currentDocument?.asos_document_filename ? (
          // Mevcut ASOS belgesi varsa
          <div className="space-y-4">
            <div className="flex items-center justify-center space-x-3">
              {getFileIcon(currentDocument.asos_document_filename)}
              <div className="text-left">
                <p className="font-medium text-sm">{currentDocument.asos_document_filename}</p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(currentDocument.asos_document_size)} • 
                  Yükleme: {new Date(currentDocument.asos_document_upload_date).toLocaleDateString('tr-TR')}
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-center space-x-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={downloading}
                className="border-orange-200 text-orange-700 hover:bg-orange-100"
              >
                <Download className="h-4 w-4 mr-1" />
                {downloading ? 'İndiriliyor...' : 'İndir'}
              </Button>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleOpenLink}
                disabled={openingLink}
                className="border-blue-200 text-blue-600 hover:bg-blue-100"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                {openingLink ? 'Açılıyor...' : 'Link'}
              </Button>
              
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadMutation.isLoading}
                className="border-orange-200 text-orange-700 hover:bg-orange-100"
              >
                <Upload className="h-4 w-4 mr-1" />
                Değiştir
              </Button>
              
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Sil
              </Button>
            </div>
          </div>
        ) : (
          // ASOS belgesi yoksa yükleme alanı
          <div className="space-y-3">
            <Upload className="h-12 w-12 mx-auto text-orange-400" />
            <div>
              <p className="text-sm font-medium text-orange-700">ASOS Belgesi Yükle</p>
              <p className="text-xs text-orange-600">
                PDF, JPG, PNG, DOC, DOCX (Maks. 10MB)
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isLoading}
              className="border-orange-200 text-orange-700 hover:bg-orange-100"
            >
              <Upload className="h-4 w-4 mr-1" />
              {uploadMutation.isLoading ? 'Yükleniyor...' : 'Dosya Seç'}
            </Button>
          </div>
        )}
      </div>

      {/* Gizli file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        onChange={handleFileSelect}
        className="hidden"
      />
      
      {/* Yardım metni */}
      <p className="text-xs text-orange-600 text-center">
        🚨 ASOS protokol belgesi, kurtarma raporu gibi acil sağlık hizmetleri dokümanlarını yükleyebilirsiniz.
      </p>
    </div>
  );
};

export default AsosDocumentUpload; 